<template>
	<div class="container pt-6 pb-10">

		<div class="flex flex-row justify-between items-center py-2 mb-4">
			<div class="flex flex-row justify-start items-center gap-8">
				<!-- "Inapoi" goes to the act's contract, not browser history -->
				<v-btn color="grey darken-1" class="uppercase" dark
					:to="'/productie/contracte/single/' + $route.params.id" exact>
					<v-icon>mdi-chevron-left</v-icon>
					Inapoi
				</v-btn>
				<h1 class="page-title-secondary">Contract productie nr.
					<span class="font-bold">{{ $route.params.id }}</span>
					- Act aditional nr. {{ aditionalExtra && aditionalExtra.id_aditional }}
				</h1>
				<span v-if="aditionalExtra && aditionalExtra.id_aditional" class="text-gray-600">
					Oferta nr. {{ aditionalExtra.previous_offer_id }} → nr. {{ aditionalExtra.new_offer_id }}
				</span>
			</div>
			<div v-if="!loading" class="flex flex-row justify-end items-center gap-2">
				<DocPDFAdditional v-if="aditionalExtra" :additional="aditionalExtra"
					:sending-email="sendingAdditionalEmail" @download="downloadPDF" @sendEmail="emailAdditionalPDF" />

				<v-btn color="teal white-text" class="uppercase" :disabled="editingMode" @click="editingMode = true">
					<v-icon>mdi-pencil</v-icon>
					Editeaza
				</v-btn>

				<!-- Only admins and superusers can delete an addendum -->
				<v-menu v-if="canDeleteAdditional" offset-y>
					<template v-slot:activator="{ on, attrs }">
						<v-btn icon v-bind="attrs" v-on="on">
							<v-icon>mdi-dots-vertical</v-icon>
						</v-btn>
					</template>
					<v-list>
						<v-list-item link @click="visibleDeleteModal = true">
							<v-icon class="mr-1">mdi-delete-forever-outline</v-icon>
							Sterge act aditional
						</v-list-item>
					</v-list>
				</v-menu>
			</div>
		</div>

		<!-- MAIN SECTION -->
		<div class="relative">
			<div v-if="editingMode"
				class="absolute top-4 right-4 rounded-lg bg-white bg-opacity-50 backdrop-filter backdrop-blur-lg shadow-lg p-2 z-10 flex gap-2">
				<v-btn color="blue-grey" class="uppercase white-text" @click="editingMode = false">Anuleaza</v-btn>
				<v-btn color="teal white-text" class="uppercase white-text"
					@click="saveAditionalContent">Salveaza</v-btn>
			</div>
			<editor v-if="aditionalConfig && aditionalExtra && aditionalExtra.id_aditional" v-model="aditional_text" :editingMode="editingMode"
				class="h-screen bg-white p-6 mb-8 overflow-y-scroll border-2 shadow-inner"
				:class="{ 'border-red-500': editingMode }" />
		</div>

		<pre v-if="$isDev" class="debug">
	<code class="content"><code>{{ contract }}</code></code>
</pre>

		<!-- Modal delete -->
		<v-dialog v-model="visibleDeleteModal" max-width="400px">
			<v-card class="p-8">
				<h6 class="text-xl font-bold mb-4">Sterge definitiv actul aditional nr. {{ aditionalExtra && aditionalExtra.id_aditional }}
					la contractul nr. {{ $route.params.id }}?</h6>
				<div class="flex flex-row gap-2">
					<v-btn color="blue-grey" class="uppercase white-text" large
						@click="visibleDeleteModal = false">Anuleaza</v-btn>
					<v-btn color="red" class="uppercase white-text" large @click="deleteAdditional()">
						<v-icon left dark>
							mdi-delete
						</v-icon>
						Sterge
					</v-btn>
				</div>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
// import moment from 'moment';
import { createAditionalConfig } from '@/documents/aditional_contract/index.js';
import Editor from "@/components/Editor";
import DocPDFAdditional from './DocPDFAdditional.vue';
import { isExportOnly } from '@/utils';

export default {
	name: "AditionalContractProductie",
	components: {
		Editor,
		DocPDFAdditional,
	},
	data() {
		return {
			aditionalConfig: null,
			visibleDeleteModal: false,
			editingMode: false,
			modifiedAditional: null,
			sendingAdditionalEmail: false,
		}
	},
	computed: {
		...mapState('contracts', {
			contract: 'singleContract',
			loading: 'loadingSingleContract',
			aditionalExtra: 'additionalDetails',
		}),
		user() {
			return this.$store.getters.loggedUser;
		},
		// Only the latest act can go: an earlier one is the base of the acts made after it
		canDeleteAdditional() {
			return ["admin", "superuser"].includes(this.user.rol) && this.aditionalExtra && this.aditionalExtra.is_latest;
		},
		firma_montaj_contract() {
			if (this.contract.detalii.firma_montaj) {
				return this.$store.getters.firma_montaj_contract(
					this.contract.detalii.firma_montaj
				);
			} else {
				return this.$store.getters.firma_montaj_activa;
			}
		},
		executantDetails() {
			return this.$store.getters.executant_details;
		},
		aditional_text: {
			get() {
				if (this.aditionalExtra && this.aditionalExtra.aditional_content) {
					// Load existing content
					return this.aditionalExtra.aditional_content;
				} else if (this.aditionalConfig) {
					// Generate initial document structure (only once)
					return this.aditionalConfig.buildDocumentStructure();
				}
				return null;
			},
			set(newVal) {
				this.modifiedAditional = newVal;
			},
		}
	},

	watch: {
		// export agents see only EU contracts: an RO one opened by link sends them back
		"contract.country": {
			handler(country) {
				if (country === "ro" && this.contract.id_contract == this.$route.params.id && isExportOnly(this.user)) {
					M.toast({ html: "Aveti acces doar la contractele EU", classes: "orange" });
					this.$router.replace("/productie/contracte/eu");
				}
			},
			immediate: true,
		},
		contract: {
			handler() {
				this.initializeAditionalConfig();
			},
			deep: true,
		},
		aditionalExtra() {
			this.initializeAditionalConfig();
		},
		"$route.params.idAditional"(id) {
			if (id) this.fetchSingleAdditionalDetails(id);
		},
	},

	methods: {
		...mapActions('contracts', ['fetchSingleContractManufacturing', 'fetchSingleAdditionalDetails', 'updateAdditionalContent', 'deleteAdditionalManufacturing']),
		toggleEditMode() {
			this.editingMode = !this.editingMode;
		},
		initializeAditionalConfig() {
			if (this.contract && this.contract.detalii && this.aditionalExtra && this.aditionalExtra.id_aditional && this.executantDetails) {
				try {
					this.aditionalConfig = createAditionalConfig(
						this.contract.country || 'ro',
						this.contract,
						this.executantDetails,
						this.firma_montaj_contract,
						this.aditionalExtra
					);
				} catch (error) {
					console.error('Error initializing aditional config:', error);
					// Fallback to Romanian if country not supported
					this.aditionalConfig = createAditionalConfig(
						'ro',
						this.contract,
						this.executantDetails,
						this.firma_montaj_contract,
						this.aditionalExtra
					);
				}
			}
		},
		denumireModulGard(str, tip_poarta) {
			if (str == "Panou") {
				str += " gard";
			}
			if (str == "Poarta_mica") {
				str += "_pietonala";
			}
			if (str == "Poarta_mare") {
				str += `_${tip_poarta}`;
			}
			let newStr = str.replace(/_/g, " ");
			return newStr;
		},
		formatParagraph(text_content, alignment, text_type) {
			return {
				type: "paragraph",
				attrs: {
					textAlign: alignment,
				},
				content: [
					{
						type: "text",
						text: text_content,
						...(text_type && { marks: [{ type: text_type }] }),
					},
				],
			};
		},
		async downloadPDF() {
			if (this.aditionalConfig) {
				const doc = await this.aditionalConfig.generatePdf(this.aditional_text);
				let filename = `Act aditional la contractul nr. ${this.contract.id_contract} din ${this.contract.data} - ${this.contract.nume}.pdf`;
				doc.save(filename);
			}
		},
		saveAditionalContent() {
			const payload = {
				id_aditional: this.aditionalExtra.id_aditional,
				id_contract: this.contract.id_contract,
				content: this.modifiedAditional,
			};
			this.updateAdditionalContent(payload);
			this.editingMode = false;
		},
		deleteAdditional() {
			const delItem = {
				id_aditional: this.aditionalExtra.id_aditional,
				id_contract: this.contract.id_contract,
				user_id: this.user.id_user,
			};
			this.deleteAdditionalManufacturing(delItem)
		},
		async emailAdditionalPDF() {
			console.log("Sending email w/ pdf...");
		}
	},

	async created() {
		// opened by link, the contract is not loaded yet
		if (this.contract.id_contract != this.$route.params.id) {
			this.fetchSingleContractManufacturing(this.$route.params.id)
		}
		await this.fetchSingleAdditionalDetails(this.$route.params.idAditional)
		this.initializeAditionalConfig()
	}
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="css" scoped>
.debug {
	padding: 1rem 0 0;
}

.debug pre {
	border-radius: 5px;
	color: #333;
}

.debug code {
	display: block;
	white-space: pre-wrap;
	font-size: 0.8rem;
	padding: 0.75rem 1rem;
	background-color: #e9ecef;
	color: #495057;
}
</style>
